/**
 * PDF ダウンロード共通ユーティリティ
 */

import { TIMING } from '@src/constants/timing';

// セキュリティ設定
export const PDF_SECURITY_CONFIG = {
  MAX_FILE_SIZE: 50 * 1024 * 1024, // 50MB
  MIN_FILE_SIZE: 100, // 100 bytes
  ALLOWED_CONTENT_TYPES: [
    'application/pdf',
    'application/x-pdf',
    'application/octet-stream', // 一部のサーバーで使用される
  ],
  TIMEOUT_MS: TIMING.API_REQUEST_TIMEOUT, // 定数を使用
};

/**
 * PDFファイルの検証
 */
export function validatePdfFile(blob: Blob, contentType: string | null): void {
  // サイズ検証
  if (blob.size < PDF_SECURITY_CONFIG.MIN_FILE_SIZE) {
    throw new Error(`PDFファイルが小さすぎます（最小${PDF_SECURITY_CONFIG.MIN_FILE_SIZE}バイト）`);
  }

  if (blob.size > PDF_SECURITY_CONFIG.MAX_FILE_SIZE) {
    throw new Error(
      `PDFファイルが大きすぎます（最大${PDF_SECURITY_CONFIG.MAX_FILE_SIZE / 1024 / 1024}MB）`
    );
  }

  // Content-Type検証
  if (contentType) {
    const isAllowedType = PDF_SECURITY_CONFIG.ALLOWED_CONTENT_TYPES.some((type) =>
      contentType.toLowerCase().includes(type)
    );

    if (!isAllowedType) {
      throw new Error(`許可されていないファイル形式です: ${contentType}`);
    }
  }

  // Blobタイプ検証
  if (
    blob.type &&
    !PDF_SECURITY_CONFIG.ALLOWED_CONTENT_TYPES.some((type) =>
      blob.type.toLowerCase().includes(type)
    )
  ) {
    console.warn(`警告: 返されたファイルのタイプが想定と異なります: ${blob.type}`);
  }
}

/**
 * ファイル名のサニタイズ
 */
export function sanitizeFileName(name: string): string {
  // 危険な文字を除去
  // biome-ignore lint/suspicious/noControlCharactersInRegex: 制御文字の除去は意図的
  return name.replace(/[<>:"/\\|?*\x00-\x1f]/g, '_').trim();
}

/**
 * レスポンスのContent-Type検証
 */
export function validateResponseContentType(contentType: string | null): void {
  if (!contentType) {
    return;
  }

  // HTMLやテキストが返された場合のエラー検出
  if (contentType.includes('text/html')) {
    throw new Error(
      'サーバーからHTMLが返されました。PDFファイルが正しく生成されていない可能性があります。'
    );
  }

  if (contentType.includes('text/plain')) {
    throw new Error('PDFではなくテキストファイルが返されました。');
  }

  if (contentType.includes('application/json')) {
    throw new Error(
      'PDFではなくJSONレスポンスが返されました。エラーレスポンスの可能性があります。'
    );
  }
}

/**
 * PDF Blobをダウンロード
 */
export function downloadPdfBlob(blob: Blob, fileName: string): void {
  // ダウンロード用のURLを作成
  const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
  const link = document.createElement('a');

  // セキュリティ属性の設定
  link.href = blobUrl;
  link.download = fileName;
  link.rel = 'noopener noreferrer'; // セキュリティ強化

  // ダウンロード実行
  document.body.appendChild(link);
  link.click();

  // クリーンアップ
  document.body.removeChild(link);

  // メモリリークを防ぐためにURLを即座に解放
  setTimeout(() => {
    window.URL.revokeObjectURL(blobUrl);
  }, 100);
}

/**
 * フェッチエラーの処理
 */
export function handleFetchError(error: unknown): string {
  let errorMessage = 'PDFダウンロードに失敗しました';

  if (error instanceof Error) {
    if (error.name === 'AbortError') {
      errorMessage = 'PDFダウンロードがタイムアウトしました';
    } else if (error.message) {
      errorMessage = `PDFダウンロードに失敗しました: ${error.message}`;
    }
  }

  return errorMessage;
}
